/* eslint-disable react-hooks/exhaustive-deps */
import {
  CircularProgress,
  Backdrop,
  Box,
  Divider,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React, { useState, useContext } from 'react';

import { StatusCode } from '../../../../../../api/enumerations';
import {
  createAnnouncement,
  deleteAnnouncement,
  updateAnnouncement,
} from '../../../../../../api/theHive/announcement';
import { AnnouncementData } from '../../../../../../api/theHive/announcement/types';
import {
  CustomTextField,
  MultilineTextField,
} from '../../../../../../components/CustomInput';
import {
  IconCloseMS,
  IconVisibilityMS,
  IconVisibilityOffMS,
} from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import ConfirmationDeleteDialog from '../ConfirmationDeleteDialog';
import ConfirmationVisibilityDialog from '../ConfirmationVisibilityDialog';
import { ViewAnnouncementsModal } from '../ViewAnnouncementsModal';
import {
  CloseButton,
  ScrollableGrid,
  StyledbBoxContainer,
  StyledDialog,
  StyledTypography,
  TitleBox,
  Title,
} from './styles';

interface NewAnnouncementModalProps {
  isModalOpen: boolean;
  announcements: AnnouncementData[];
  setAnnouncements: React.Dispatch<React.SetStateAction<AnnouncementData[]>>;
  onCloseModal: () => void;
  onAnnouncementUpdated: () => Promise<void>;
  noticeId: number;
}

export function NewAnnouncementModal({
  isModalOpen,
  onCloseModal,
  onAnnouncementUpdated,
  announcements,
  setAnnouncements,
  noticeId,
}: NewAnnouncementModalProps): JSX.Element {
  const [showAnnModal, setShowAnnModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementData | null>(null);
  const [subject, setSubject] = useState('');
  const [contents, setContents] = useState('');
  const [loading, setLoading] = useState(false);
  const [openVisibility, setOpenVisibility] = useState(false);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const handleClose = (): void => setOpenVisibility(false);
  const handleOpen = (announcement: AnnouncementData): void => {
    setSelectedAnnouncement(announcement);
    setOpenVisibility(true);
  };

  const handleAnnouncement = async (): Promise<void> => {
    if (!selectedAnnouncement) return;

    try {
      const response = await updateAnnouncement(selectedAnnouncement.id, {
        is_visible: !selectedAnnouncement.is_visible,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('Visibilidade alterada com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      onAnnouncementUpdated();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleDelete = async (annId: number): Promise<void> => {
    try {
      const response = await deleteAnnouncement(annId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setSnackbarMessage('Comunicado excluído!');
      setErrorMessage(false);
      setOpenSnackbar(true);

      setAnnouncements((prev) => prev.filter((item) => item.id !== annId));
      onAnnouncementUpdated();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleShowAnnModal = (announcement: AnnouncementData): void => {
    setSelectedAnnouncement(announcement);
    setShowAnnModal(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (subject.trim() === '' || contents.trim() === '') {
      setSnackbarMessage('Preencha assunto e comunicado.');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await createAnnouncement(noticeId, {
        subject,
        contents,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setSnackbarMessage('Comunicado adicionado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);

      setSubject('');
      setContents('');
      onAnnouncementUpdated();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StyledDialog open={isModalOpen} onClose={onCloseModal}>
        <StyledbBoxContainer>
          <TitleBox>
            <Title>Novo comunicado</Title>
            <CloseButton
              onClick={onCloseModal}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              {IconCloseMS}
            </CloseButton>
          </TitleBox>
          <Box width="400px">
            <CustomTextField
              id="subject"
              label="Assunto"
              value={subject}
              setValue={setSubject}
              maxLength={20}
              required
            />
          </Box>
          <MultilineTextField
            id="contents"
            label="Comunicado*"
            value={contents}
            setValue={setContents}
            maxLength={1000}
          />
          <Box display="flex" flexDirection="row-reverse">
            <Button disabled={loading} onClick={handleSubmit}>
              adicionar comunicado
            </Button>
          </Box>
          <Title sx={{ color: '#595959' }}>Comunicados existentes</Title>
          <ScrollableGrid sx={{ opacity: loading ? 0.5 : 1 }}>
            {announcements.length === 0 ? (
              <Typography textAlign="center">
                Nenhum comunicado cadastrado.
              </Typography>
            ) : (
              announcements.map((announcement) => (
                <React.Fragment key={announcement.id}>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <StyledTypography
                      onClick={() => handleShowAnnModal(announcement)}
                    >
                      {announcement.subject.length > 20
                        ? `${announcement.subject.substring(0, 20)}...`
                        : announcement.subject}
                    </StyledTypography>
                    <Box display="flex" mr={2} alignItems="center">
                      <ConfirmationDeleteDialog
                        iconDelete
                        text="excluir este comunicado"
                        modalCallback={() => handleDelete(announcement.id)}
                      />
                      <Tooltip
                        title={
                          announcement.is_visible
                            ? 'omitir comunicado'
                            : 'exibir comunicado'
                        }
                      >
                        <IconButton
                          sx={{ color: 'secondary.main', height: '40px' }}
                          onClick={() => handleOpen(announcement)}
                        >
                          {announcement.is_visible
                            ? IconVisibilityOffMS
                            : IconVisibilityMS}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </React.Fragment>
              ))
            )}
          </ScrollableGrid>
          <Backdrop
            open={loading}
            sx={(theme) => ({
              backgroundColor: '#ffffff66',
              zIndex: theme.zIndex.drawer + 1,
            })}
          >
            <CircularProgress sx={{ color: 'primary.light' }} />
          </Backdrop>
        </StyledbBoxContainer>
      </StyledDialog>
      {selectedAnnouncement && (
        <ViewAnnouncementsModal
          title={selectedAnnouncement.subject}
          announcement={selectedAnnouncement}
          onCloseModal={() => setSelectedAnnouncement(null)}
          isModalOpen={showAnnModal}
        />
      )}
      {openVisibility && (
        <ConfirmationVisibilityDialog
          text={selectedAnnouncement?.is_visible ? 'omitir' : 'exibir'}
          modalCallback={handleAnnouncement}
          open={openVisibility}
          handleClose={handleClose}
        />
      )}
    </>
  );
}
